/**
 * Materials library page
 */

import React, { useState, useEffect } from 'react';
import { Button, Input, Select, Table, Space, Tag, Image, message, Dropdown, Menu, Tree, Modal } from 'antd';
import { PlusOutlined, MoreOutlined, EditOutlined, DeleteOutlined, UploadOutlined, FolderOutlined, ExclamationCircleOutlined, FileExcelOutlined, FileWordOutlined, FilePdfOutlined, FilePptOutlined, FileZipOutlined, FileTextOutlined, FileOutlined, PlayCircleOutlined, SoundOutlined } from '@ant-design/icons';
import { materialApi, folderApi } from '../../services/resourceService';
import { getCurrentUser } from '../../services/auth';
import { getUsers } from '../../services/user';
import { Material, ResourceFolder, ResourceScope, MaterialType, FolderType } from '../../types/resource';
import CreateMaterialModal from './CreateMaterialModal';
import EditMaterialModal from './EditMaterialModal';
import BatchUploadModal from './BatchUploadModal';
import CreateFolderModal from './CreateFolderModal';
import EditFolderModal from './EditFolderModal';
import './Materials.css';

const { Search } = Input;
const { Option } = Select;
const { confirm } = Modal;

// Build tree structure from flat folder list
const buildFolderTree = (folders: ResourceFolder[], onEdit: (folder: ResourceFolder) => void, onDelete: (folder: ResourceFolder) => void) => {
  const folderMap = new Map<number, any>();
  const rootFolders: any[] = [];

  // Create tree nodes
  folders.forEach((folder) => {
    folderMap.set(folder.id, {
      key: folder.id,
      title: (
        <div className="folder-tree-node">
          <span className="folder-name">{folder.name}</span>
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item
                  key="edit"
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.domEvent.stopPropagation();
                    onEdit(folder);
                  }}
                >
                  编辑
                </Menu.Item>
                <Menu.Item
                  key="delete"
                  icon={<DeleteOutlined />}
                  danger
                  onClick={(e) => {
                    e.domEvent.stopPropagation();
                    onDelete(folder);
                  }}
                >
                  删除
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <MoreOutlined
              className="folder-actions"
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        </div>
      ),
      icon: <FolderOutlined />,
      folder: folder,
      children: [],
    });
  });

  // Build tree structure
  folders.forEach((folder) => {
    const node = folderMap.get(folder.id);
    if (folder.parent_id && folderMap.has(folder.parent_id)) {
      const parent = folderMap.get(folder.parent_id);
      parent.children.push(node);
    } else {
      rootFolders.push(node);
    }
  });

  return rootFolders;
};

const MaterialsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ResourceScope>(ResourceScope.ENTERPRISE);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Material[]>([]);
  const [total, setTotal] = useState(0);
  const [folders, setFolders] = useState<ResourceFolder[]>([]);
  const [folderTree, setFolderTree] = useState<any[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<number | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [batchUploadModalVisible, setBatchUploadModalVisible] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
  const [folderModalVisible, setFolderModalVisible] = useState(false);
  const [editFolderModalVisible, setEditFolderModalVisible] = useState(false);
  const [editingFolder, setEditingFolder] = useState<ResourceFolder | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(undefined);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Load folders
  const loadFolders = async () => {
    try {
      const result = await folderApi.getList({
        folder_type: 'material',
        scope: activeTab,
        owner_id: selectedUserId,
      });
      setFolders(result.items);
      // Build tree structure with edit/delete handlers
      const tree = buildFolderTree(
        result.items,
        (folder) => {
          setEditingFolder(folder);
          setEditFolderModalVisible(true);
        },
        handleDeleteFolder
      );
      setFolderTree(tree);
      // Auto expand all folders
      const allKeys = result.items.map(f => f.id);
      setExpandedKeys(allKeys);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  // Load materials data
  const loadData = async () => {
    setLoading(true);
    try {
      const result = await materialApi.getList({
        page,
        page_size: pageSize,
        scope: activeTab,
        folder_id: selectedFolder || undefined,
        owner_id: selectedUserId,
        keyword: searchKeyword || undefined,
      });
      console.log('=== 素材列表数据 ===');
      console.log('总数:', result.total);
      console.log('数据:', result.items);
      if (result.items.length > 0) {
        console.log('第一条素材:', result.items[0]);
        console.log('file_url:', result.items[0].file_url);
        console.log('thumbnail_url:', result.items[0].thumbnail_url);
      }
      setData(result.items);
      setTotal(result.total);
    } catch (error) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  // Load users (for admin)
  const loadUsers = async () => {
    try {
      const result = await getUsers({ skip: 0, limit: 1000 });
      // Backend returns array directly, not { items: [] } format
      const userList = Array.isArray(result) ? result : (result.items || []);
      setUsers(userList);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  // Load current user
  const loadCurrentUser = async () => {
    try {
      const user = await getCurrentUser();
      setCurrentUser(user);
      // If admin, load users list
      if (user.role === 'admin') {
        loadUsers();
      }
    } catch (error) {
      console.error('Failed to load current user:', error);
    }
  };

  // Initial load
  useEffect(() => {
    loadCurrentUser();
  }, []);

  // Load data when params change
  useEffect(() => {
    loadData();
  }, [page, pageSize, activeTab, selectedFolder, searchKeyword, selectedUserId]);

  // Load folders when tab changes
  useEffect(() => {
    loadFolders();
    setSelectedFolder(null);
    setSelectedUserId(undefined);
    setPage(1);
  }, [activeTab]);

  // Reload folders when selected user changes (for admin viewing other users' personal folders)
  useEffect(() => {
    if (activeTab === ResourceScope.PERSONAL && selectedUserId !== undefined) {
      loadFolders();
      setSelectedFolder(null);
    }
  }, [selectedUserId]);

  // Handle delete
  const handleDelete = async (id: number) => {
    try {
      await materialApi.delete(id);
      message.success('删除成功');
      loadData();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // Handle delete folder
  const handleDeleteFolder = async (folder: ResourceFolder) => {
    confirm({
      title: '确认删除',
      icon: <ExclamationCircleOutlined />,
      content: `确定要删除分组"${folder.name}"吗？`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await folderApi.delete(folder.id);
          message.success('删除成功');
          loadFolders();
          if (selectedFolder === folder.id) {
            setSelectedFolder(null);
          }
        } catch (error: any) {
          message.error(error.response?.data?.detail || '删除失败');
        }
      },
    });
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get material type config
  const getMaterialTypeConfig = (type: MaterialType) => {
    const typeMap = {
      [MaterialType.IMAGE]: { text: '图片', color: 'blue' },
      [MaterialType.VIDEO]: { text: '视频', color: 'purple' },
      [MaterialType.AUDIO]: { text: '音频', color: 'orange' },
      [MaterialType.DOCUMENT]: { text: '文档', color: 'green' },
      [MaterialType.OTHER]: { text: '其他', color: 'default' },
    };
    return typeMap[type] || typeMap[MaterialType.OTHER];
  };

  // Table columns
  const columns = [
    {
      title: '预览',
      dataIndex: 'thumbnail_url',
      key: 'thumbnail_url',
      width: 80,
      render: (url: string, record: Material) => {
        // 优先使用 thumbnail_url，没有就用 file_url
        const imageUrl = url || record.file_url;
        
        // 判断是否为图片类型（包括 webp）
        const isImage = record.file_type === MaterialType.IMAGE || 
                        record.content_type?.startsWith('image/') ||
                        record.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/);
        
        if (isImage && imageUrl) {
          return (
            <Image
              src={imageUrl}
              width={50}
              height={50}
              style={{ objectFit: 'cover', borderRadius: 4 }}
              preview={{ src: record.file_url || imageUrl }}
            />
          );
        }
        
        // 根据文件类型或扩展名获取图标
        const ext = record.name.toLowerCase().split('.').pop() || '';
        const iconStyle = { fontSize: 32 };
        
        // Excel
        if (['xls', 'xlsx', 'csv'].includes(ext)) {
          return (
            <div style={{ width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f9ff', borderRadius: 4 }}>
              <FileExcelOutlined style={{ ...iconStyle, color: '#52c41a' }} />
            </div>
          );
        }
        // Word
        if (['doc', 'docx'].includes(ext)) {
          return (
            <div style={{ width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f5ff', borderRadius: 4 }}>
              <FileWordOutlined style={{ ...iconStyle, color: '#1890ff' }} />
            </div>
          );
        }
        // PDF
        if (ext === 'pdf') {
          return (
            <div style={{ width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff1f0', borderRadius: 4 }}>
              <FilePdfOutlined style={{ ...iconStyle, color: '#ff4d4f' }} />
            </div>
          );
        }
        // PPT
        if (['ppt', 'pptx'].includes(ext)) {
          return (
            <div style={{ width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff7e6', borderRadius: 4 }}>
              <FilePptOutlined style={{ ...iconStyle, color: '#fa8c16' }} />
            </div>
          );
        }
        // 压缩文件
        if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
          return (
            <div style={{ width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9f0ff', borderRadius: 4 }}>
              <FileZipOutlined style={{ ...iconStyle, color: '#722ed1' }} />
            </div>
          );
        }
        // 文本文件
        if (['txt', 'md', 'json', 'xml'].includes(ext)) {
          return (
            <div style={{ width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', borderRadius: 4 }}>
              <FileTextOutlined style={{ ...iconStyle, color: '#8c8c8c' }} />
            </div>
          );
        }
        // 视频
        if (record.file_type === MaterialType.VIDEO || ['mp4', 'avi', 'mov', 'wmv', 'flv'].includes(ext)) {
          return (
            <div style={{ width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f5ff', borderRadius: 4 }}>
              <PlayCircleOutlined style={{ ...iconStyle, color: '#1890ff' }} />
            </div>
          );
        }
        // 音频
        if (record.file_type === MaterialType.AUDIO || ['mp3', 'wav', 'ogg', 'aac'].includes(ext)) {
          return (
            <div style={{ width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9f0ff', borderRadius: 4 }}>
              <SoundOutlined style={{ ...iconStyle, color: '#722ed1' }} />
            </div>
          );
        }
        // 默认文件图标
        return (
          <div style={{ width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', borderRadius: 4 }}>
            <FileOutlined style={{ ...iconStyle, color: '#8c8c8c' }} />
          </div>
        );
      },
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: '30%',
    },
    {
      title: '类型',
      dataIndex: 'file_type',
      key: 'file_type',
      width: 100,
      render: (type: MaterialType) => {
        const config = getMaterialTypeConfig(type);
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '大小',
      dataIndex: 'file_size',
      key: 'file_size',
      width: 100,
      render: (size: number) => formatFileSize(size),
    },
    {
      title: '使用次数',
      dataIndex: 'usage_count',
      key: 'usage_count',
      width: 100,
      sorter: true,
      render: (count: number) => (
        <span style={{ color: count > 0 ? '#1890ff' : undefined }}>
          {count}
        </span>
      ),
    },
    {
      title: '创建人',
      dataIndex: 'owner_name',
      key: 'owner_name',
      width: 120,
      render: (name: string) => name || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: Material) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item 
                key="edit" 
                icon={<EditOutlined />}
                onClick={() => {
                  setSelectedMaterial(record);
                  setEditModalVisible(true);
                }}
              >
                编辑
              </Menu.Item>
              <Menu.Item
                key="delete"
                icon={<DeleteOutlined />}
                danger
                onClick={() => handleDelete(record.id)}
              >
                删除
              </Menu.Item>
            </Menu>
          }
          trigger={['click']}
        >
          <Button type="text" size="small" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="materials-page">
      {/* Header with title and tabs */}
      <div className="page-header">
        <div className="header-left">
          <h2>素材库</h2>
          <div className="scope-tabs">
            <div
              className={`tab-item ${activeTab === ResourceScope.ENTERPRISE ? 'active' : ''}`}
              onClick={() => setActiveTab(ResourceScope.ENTERPRISE)}
            >
              企业素材
            </div>
            <div
              className={`tab-item ${activeTab === ResourceScope.PERSONAL ? 'active' : ''}`}
              onClick={() => setActiveTab(ResourceScope.PERSONAL)}
            >
              个人素材
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="page-content">
        {/* Left sidebar - Folders */}
        <div className="folder-sidebar">
          <div className="folder-header">
            <span>分组</span>
            <Button 
              type="text" 
              size="small" 
              icon={<PlusOutlined />}
              onClick={() => setFolderModalVisible(true)}
            >
              添加分组
            </Button>
          </div>
          <div className="folder-list">
            <div
              className={`folder-item ${selectedFolder === null ? 'active' : ''}`}
              onClick={() => setSelectedFolder(null)}
            >
              <span>全部分组</span>
              <span className="folder-count">{total}</span>
            </div>
            {folderTree.length > 0 && (
              <Tree
                showIcon
                expandedKeys={expandedKeys}
                onExpand={(keys) => setExpandedKeys(keys)}
                selectedKeys={selectedFolder ? [selectedFolder] : []}
                onSelect={(keys) => {
                  if (keys.length > 0) {
                    setSelectedFolder(keys[0] as number);
                  }
                }}
                treeData={folderTree}
                className="folder-tree"
              />
            )}
          </div>
        </div>

        {/* Right content - Table */}
        <div className="main-content">
          {/* Toolbar */}
          <div className="content-toolbar">
            <Space wrap>
              <Search
                placeholder="搜索素材名称..."
                allowClear
                style={{ width: 250, minWidth: 150 }}
                onSearch={setSearchKeyword}
              />
              {activeTab === ResourceScope.PERSONAL && currentUser?.role === 'admin' && (
                <Select
                  placeholder="选择用户"
                  allowClear
                  style={{ width: 120, minWidth: 100 }}
                  value={selectedUserId}
                  onChange={(value) => setSelectedUserId(value)}
                >
                  {users && users.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.real_name || user.username}
                    </Option>
                  ))}
                </Select>
              )}
              <Select
                value={sortBy}
                onChange={setSortBy}
                style={{ width: 120, minWidth: 100 }}
              >
                <Option value="created_at">按创建时间</Option>
                <Option value="usage_count">按使用次数</Option>
                <Option value="file_size">按文件大小</Option>
              </Select>
            </Space>
            <Space wrap>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => setCreateModalVisible(true)}
              >
                上传素材
              </Button>
              <Button 
                icon={<UploadOutlined />}
                onClick={() => setBatchUploadModalVisible(true)}
              >
                批量上传
              </Button>
            </Space>
          </div>

          {/* Table */}
          <Table
            loading={loading}
            dataSource={data}
            columns={columns}
            rowKey="id"
            scroll={{ x: 1200, y: 'calc(100vh - 380px)' }}
            pagination={{
              current: page,
              pageSize: pageSize,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, pageSize) => {
                setPage(page);
                setPageSize(pageSize);
              },
            }}
          />
        </div>
      </div>

      {/* Create Modal */}
      <CreateMaterialModal
        visible={createModalVisible}
        scope={activeTab}
        onCancel={() => setCreateModalVisible(false)}
        onSuccess={() => {
          setCreateModalVisible(false);
          loadData();
        }}
      />

      {/* Edit Modal */}
      <EditMaterialModal
        visible={editModalVisible}
        material={selectedMaterial}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedMaterial(null);
        }}
        onSuccess={() => {
          setEditModalVisible(false);
          setSelectedMaterial(null);
          loadData();
        }}
      />

      {/* Batch Upload Modal */}
      <BatchUploadModal
        visible={batchUploadModalVisible}
        scope={activeTab}
        onCancel={() => setBatchUploadModalVisible(false)}
        onSuccess={() => {
          setBatchUploadModalVisible(false);
          loadData();
        }}
      />

      {/* Folder Modal */}
      <CreateFolderModal
        visible={folderModalVisible}
        folderType={FolderType.MATERIAL}
        scope={activeTab}
        onCancel={() => setFolderModalVisible(false)}
        onSuccess={() => {
          setFolderModalVisible(false);
          loadFolders();
        }}
      />

      {/* Edit Folder Modal */}
      <EditFolderModal
        visible={editFolderModalVisible}
        folder={editingFolder}
        allFolders={folders}
        onCancel={() => {
          setEditFolderModalVisible(false);
          setEditingFolder(null);
        }}
        onSuccess={() => {
          setEditFolderModalVisible(false);
          setEditingFolder(null);
          loadFolders();
        }}
      />
    </div>
  );
};

export default MaterialsPage;
